import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Box, Text, Group, SimpleGrid, Loader, Center, Stack } from '@mantine/core'
import {
  IconDeviceLaptop, IconDeviceMobile, IconFileText, IconKey,
  IconTool, IconPackage, IconChevronRight,
} from '@tabler/icons-react'
import {
  getDashboard, getContractSummary, getLicenseSummary,
  getInventorySummary, getRecentActivity,
} from '../api/client'

interface Stats {
  totalAssets: number
  activeContracts: number
  licenseExpiring30: number
  repairPending: number
  lowStock: number
}

// แปลง movement type ให้เป็นข้อความอ่านง่าย — ไม่ได้ครอบคลุมทุก type ในระบบ
// (มีเยอะมาก) เอาเฉพาะที่เจอบ่อยสุดในหน้า Recent Activities จริงๆ ที่เหลือ fallback ไปใช้ note/type ดิบ
function describeMovement(m: any, t: any): string {
  const map: Record<string, string> = {
    DEPT_TRANSFER:   t('mobileDashboard.mvDeptTransfer', 'Transferred to') + ` ${m.toValue ?? ''}`,
    LOCATION_CHANGE: t('mobileDashboard.mvLocationChange', 'Moved to') + ` ${m.toValue ?? ''}`,
    LOCATION_MOVE:   t('mobileDashboard.mvLocationChange', 'Moved to') + ` ${m.toValue ?? ''}`,
    OWNER_CHANGE:    t('mobileDashboard.mvOwnerChange', 'Owner changed to') + ` ${m.toValue ?? ''}`,
    STATUS_CHANGE:   t('mobileDashboard.mvStatusChange', 'Status changed to') + ` ${m.toValue ?? ''}`,
    HANDOVER:        t('mobileDashboard.mvHandover', 'Handed over to') + ` ${m.toValue ?? ''}`,
    HANDOVER_RETURN: t('mobileDashboard.mvHandoverReturn', 'Handover returned'),
    BORROW:          t('mobileDashboard.mvBorrow', 'Borrowed by') + ` ${m.toValue ?? ''}`,
    BORROW_APPROVED: t('mobileDashboard.mvBorrowApproved', 'Borrow approved'),
    BORROW_REJECTED: t('mobileDashboard.mvBorrowRejected', 'Borrow rejected'),
    RETURN:          t('mobileDashboard.mvReturn', 'Returned'),
    REPAIR_OPENED:   t('mobileDashboard.mvRepairOpened', 'Sent for repair'),
    REPAIR_DONE:     t('mobileDashboard.mvRepairDone', 'Repair completed'),
    WRITTEN_OFF:     t('mobileDashboard.mvWrittenOff', 'Written off'),
    DAMAGED:         t('mobileDashboard.mvDamaged', 'Reported damaged'),
    ASSET_CREATED:   t('mobileDashboard.mvAssetCreated', 'Asset created'),
  }
  return map[m.type] ?? (m.note || m.type)
}

function timeAgo(dateStr: string, t: any): string {
  const diffMs = Date.now() - new Date(dateStr).getTime()
  const mins = Math.floor(diffMs / 60000)
  if (mins < 1) return t('mobileDashboard.justNow', 'Just now')
  if (mins < 60) return t('mobileDashboard.minsAgo', { count: mins, defaultValue: `${mins} min ago` })
  const hrs = Math.floor(mins / 60)
  if (hrs < 24) return t('mobileDashboard.hoursAgo', { count: hrs, defaultValue: `${hrs} hr ago` })
  const days = Math.floor(hrs / 24)
  return t('mobileDashboard.daysAgo', { count: days, defaultValue: `${days}d ago` })
}

export default function MobileDashboardPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [loading, setLoading] = useState(true)
  const [stats, setStats] = useState<Stats>({ totalAssets: 0, activeContracts: 0, licenseExpiring30: 0, repairPending: 0, lowStock: 0 })
  const [activities, setActivities] = useState<any[]>([])

  useEffect(() => {
    const load = async () => {
      setLoading(true)
      try {
        const [dash, contracts, licenses, inv, recent] = await Promise.all([
          getDashboard().catch(() => ({ data: {} })),
          getContractSummary().catch(() => ({ data: {} })),
          getLicenseSummary().catch(() => ({ data: {} })),
          getInventorySummary().catch(() => ({ data: {} })),
          getRecentActivity(10).catch(() => ({ data: [] })),
        ])
        setStats({
          totalAssets: dash.data?.totalAssets ?? 0,
          activeContracts: contracts.data?.active ?? 0,
          licenseExpiring30: licenses.data?.expiring ?? 0,
          repairPending: dash.data?.pendingMaintenance ?? 0,
          lowStock: inv.data?.lowStockCount ?? 0,
        })
        setActivities(recent.data ?? [])
      } finally { setLoading(false) }
    }
    load()
  }, [])

  const statBoxes = [
    { key: 'contracts', label: t('mobileDashboard.contracts', 'Contracts'), value: stats.activeContracts, icon: IconFileText, color: '#7c3aed', bg: '#f5f3ff', path: '/licenses' },
    { key: 'license',   label: t('mobileDashboard.licenseExpiring', 'License Expiring 30d'), value: stats.licenseExpiring30, icon: IconKey, color: '#f59e0b', bg: '#fffbeb', path: '/licenses' },
    { key: 'repair',    label: t('mobileDashboard.repairPending', 'Repair Pending'), value: stats.repairPending, icon: IconTool, color: '#dc2626', bg: '#fef2f2', path: '/maintenance' },
    { key: 'stock',     label: t('mobileDashboard.warehouse', 'Warehouse'), value: stats.lowStock, icon: IconPackage, color: '#059669', bg: '#f0fdf4', path: '/inventory' },
  ]

  if (loading) {
    return <Center py="xl" style={{ minHeight: '60vh' }}><Loader color="teal" /></Center>
  }

  return (
    <Box p={16}>
      {/* ── Total Assets hero card → tap to Assets page ── */}
      <Box
        onClick={() => navigate('/assets')}
        style={{
          background: 'linear-gradient(135deg, #0d9488, #0f172a)',
          borderRadius: 20, padding: 20, cursor: 'pointer', position: 'relative', overflow: 'hidden',
        }}>
        <Text size="sm" c="white" style={{ opacity: 0.85 }}>{t('mobileDashboard.totalAssets', 'Total Assets')}</Text>
        <Text size="32px" fw={700} c="white" mt={4}>{stats.totalAssets.toLocaleString()}</Text>
        <Box style={{ position: 'absolute', right: 16, bottom: 12, opacity: 0.35, display: 'flex' }}>
          <IconDeviceMobile size={54} color="#fff" style={{ marginRight: -14, transform: 'rotate(-6deg)' }} />
          <IconDeviceLaptop size={72} color="#fff" />
        </Box>
      </Box>

      {/* ── 4 stat boxes ── */}
      <SimpleGrid cols={2} spacing="sm" mt="md">
        {statBoxes.map((box) => (
          <Box key={box.key} onClick={() => navigate(box.path)}
            style={{ background: box.bg, borderRadius: 16, padding: 14, cursor: 'pointer' }}>
            <Group justify="space-between" mb={6}>
              <Text size="xs" fw={600} c="dimmed" style={{ maxWidth: 100 }}>{box.label}</Text>
              <box.icon size={16} color={box.color} />
            </Group>
            <Text size="24px" fw={700} style={{ color: box.color }}>{box.value}</Text>
          </Box>
        ))}
      </SimpleGrid>

      {/* ── Recent Activities ── */}
      <Group justify="space-between" mt="lg" mb="sm">
        <Text fw={600} size="sm">{t('mobileDashboard.recentActivities', 'Recent Activities')}</Text>
      </Group>
      {activities.length === 0 ? (
        <Text size="sm" c="dimmed" ta="center" py="xl">{t('mobileDashboard.noActivity', 'No recent activity')}</Text>
      ) : (
        <Stack gap={8}>
          {activities.map((a: any) => (
            <Group key={a.id} justify="space-between" wrap="nowrap"
              style={{ background: '#fff', borderRadius: 14, border: '1px solid #ebebeb', padding: 12 }}>
              <Box style={{ flex: 1, minWidth: 0 }}>
                <Text size="sm" fw={600} truncate>{a.asset?.name ?? a.assetCode ?? '—'}</Text>
                <Text size="xs" c="dimmed" truncate>{describeMovement(a, t)}</Text>
                <Text size="xs" c="dimmed" mt={2}>{timeAgo(a.createdAt, t)}</Text>
              </Box>
              <IconChevronRight size={16} color="#cbd5e1" />
            </Group>
          ))}
        </Stack>
      )}
    </Box>
  )
}
